import { useState } from "react";
import { coloringApi } from "./api.js";
import { useColoring } from "./ColoringContext.js";
import { AiButton, AutoField, SectionCard, useAction } from "./fields.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { GenerationSettings } from "./GenerationSettings.js";
import type { StyleBible } from "./types.js";

const FIELDS: { key: keyof Omit<StyleBible, "pageBorder">; label: string; rows: number; hint?: string }[] = [
  { key: "visualStyle", label: "Visual style", rows: 3, hint: "Describe the look in your own words. Prefer descriptive attributes (shapes, line quality, mood) over naming a franchise or living artist." },
  { key: "lineWeight", label: "Line weight", rows: 2 },
  { key: "complexity", label: "Complexity", rows: 2 },
  { key: "characterProportions", label: "Character proportions", rows: 2 },
  { key: "backgroundComplexity", label: "Background complexity", rows: 2 },
  { key: "openSpace", label: "Amount of open coloring space", rows: 2 },
  { key: "shadingRules", label: "Shading rules", rows: 2 },
  { key: "perspectiveRules", label: "Perspective rules", rows: 2 },
  { key: "composition", label: "Composition preferences", rows: 2 },
  { key: "avoid", label: "Things to avoid", rows: 3, hint: "Also added to every page's negative prompt." }
];

/** The Style Bible: reused programmatically in every page's prompt. */
export function StylePanel() {
  const { projectId, state, saveBook, reload } = useColoring();
  const { book } = state;
  const { busy, run } = useAction();
  const [confirm, setConfirm] = useState(false);

  const generate = async () => {
    setConfirm(false);
    await run("style", async () => {
      await coloringApi.generateStyleBible(projectId);
      await reload();
    }, "Style Bible generated — edit anything you like.");
  };

  return (
    <div className="coloring-scroll">
      <div className="coloring-grid">
        <SectionCard
          className="span-2"
          icon="layers"
          title="Style Bible"
          actions={
            <AiButton busy={busy === "style"} onClick={() => setConfirm(true)} disabled={!(book.concept ?? "").trim()} title={!(book.concept ?? "").trim() ? "Write a concept on the Overview first" : undefined}>
              Generate Style Bible
            </AiButton>
          }
        >
          <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
            These rules are added to <strong>every</strong> page's prompt, so the whole book shares one look. Defaults describe a traditional
            black-line coloring book; change anything.
          </p>
          <div className="coloring-fields">
            {FIELDS.map((f) => (
              <AutoField
                key={f.key}
                label={f.label}
                rows={f.rows}
                hint={f.hint}
                value={book.style[f.key]}
                onSave={(v) => saveBook({ style: { [f.key]: v } })}
              />
            ))}
          </div>
          <label className="pdf-toggle" style={{ marginTop: 6 }}>
            <input type="checkbox" checked={book.style.pageBorder} onChange={(e) => void saveBook({ style: { pageBorder: e.target.checked } })} />
            <span>Draw a decorative border around each page</span>
          </label>
        </SectionCard>
      </div>
      <div className="coloring-grid" style={{ marginTop: 18 }}>
        <GenerationSettings />
      </div>
      {confirm && (
        <ConfirmDialog
          title="Generate Style Bible?"
          message="This replaces the current Style Bible text with a new version written from your concept. You can edit it afterwards."
          confirmLabel="Generate"
          onConfirm={() => void generate()}
          onCancel={() => setConfirm(false)}
        />
      )}
    </div>
  );
}
